import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Truck, Calendar, User, Clock } from 'lucide-react';
import { api } from '../lib/auth';

/**
 * UserVehicleHistory — Çalışanın atandığı ve geçmişte atandığı araç(lar)ı
 * gösterir. Aktif atama vurgulanır (emerald), geçmiş kayıtlar timeline
 * olarak listelenir.
 *
 * Kullanım yerleri:
 *   - AdminEmployeeProfile.jsx (admin başkasının profiline bakarken)
 *   - UserProfile.jsx (kullanıcı kendi profilinde)
 *
 * Backend: GET /api/users/{userId}/vehicles
 *   Hem aktif hem kapanmış atamaları (newest first) döner.
 *   Her item `vehicle: {id, license_plate, brand, model, firm_name}` içerir.
 */
export function UserVehicleHistory({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/users/${userId}/vehicles`)
      .then((res) => setItems(res.data?.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Truck className="h-4 w-4 text-blue-500" /> Araç Geçmişi
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-slate-400">Yükleniyor…</CardContent>
      </Card>
    );
  }

  const active = items.find((a) => !a.unassigned_at);
  const past = items.filter((a) => a.unassigned_at);

  return (
    <Card data-testid={`user-vehicle-history-${userId}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Truck className="h-4 w-4 text-blue-500" />
          Araç Geçmişi
          {items.length > 0 && (
            <Badge variant="outline" className="ml-1 text-[10px]">{items.length}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 ? (
          <div className="text-xs text-slate-400 italic">Henüz atanmış araç yok</div>
        ) : (
          <>
            {active && <ActiveCard a={active} />}
            {past.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Geçmiş</div>
                <ul className="space-y-2">
                  {past.map((a) => (
                    <PastRow key={a.id} a={a} />
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ActiveCard({ a }) {
  const v = a.vehicle || {};
  return (
    <div className="rounded-lg border-2 border-emerald-300 dark:border-emerald-700/60 bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/30 dark:to-emerald-900/10 p-3" data-testid="user-vehicle-active">
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 shadow-sm">
          <Truck className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-base text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 px-2 py-0.5 rounded shadow-sm" data-testid="user-vehicle-active-plate">
              {a.license_plate || v.license_plate || '—'}
            </span>
            <Badge className="bg-emerald-500 text-white text-[10px]">Aktif</Badge>
          </div>
          <div className="text-xs text-slate-700 dark:text-slate-200 mt-1 truncate">
            {[v.brand, v.model].filter(Boolean).join(' ')}
            {v.firm_name && <span className="text-slate-500"> · {v.firm_name}</span>}
          </div>
          <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-1.5 flex-wrap">
            <span className="inline-flex items-center gap-0.5">
              <Calendar className="h-3 w-3" />
              {new Date(a.assigned_at).toLocaleDateString('tr')}
            </span>
            {a.assigned_by_full_name && (
              <span className="inline-flex items-center gap-0.5">
                <User className="h-3 w-3" />
                {a.assigned_by_full_name}
              </span>
            )}
          </div>
          {a.note && (
            <div className="mt-1.5 text-[11px] text-slate-600 dark:text-slate-300 italic px-2 py-1 rounded bg-white/60 dark:bg-slate-800/60">
              "{a.note}"
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PastRow({ a }) {
  const v = a.vehicle || {};
  return (
    <li className="border border-slate-200 dark:border-slate-700 rounded p-2 text-xs flex items-start gap-2" data-testid={`user-vehicle-past-${a.id}`}>
      <Clock className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
            {a.license_plate || v.license_plate || '—'}
          </span>
          {[v.brand, v.model].filter(Boolean).length > 0 && (
            <span className="text-slate-500">{[v.brand, v.model].filter(Boolean).join(' ')}</span>
          )}
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5">
          {new Date(a.assigned_at).toLocaleDateString('tr')} →{' '}
          {a.unassigned_at ? new Date(a.unassigned_at).toLocaleDateString('tr') : '—'}
          {a.unassigned_reason === 'reassigned' && (
            <span className="ml-1 text-amber-600">· yeniden atandı</span>
          )}
        </div>
        {a.note && (
          <div className="text-[10px] text-slate-500 italic mt-0.5">"{a.note}"</div>
        )}
      </div>
    </li>
  );
}
